import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { Server, RefreshCw, Circle, ExternalLink } from 'lucide-react';
import { api } from '@/lib/api-client';
import { capabilityColors } from '@/lib/capability-colors';
import { capabilityDetailPath } from '@/types/capability-types';
import type { Capability } from '@/types/capability-types';
import { usePageTitle } from '@/hooks/use-page-title';
import { useHeaderAction } from '@/lib/header-action-context';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { CapabilityListPanel } from '@/components/capability-list/CapabilityListPanel';
import { listControlsGlassTrigger } from '@/components/capability-list/list-controls-classes';
import { cn } from '@/lib/utils';

interface WorkerCapability extends Capability {
    config: {
        capabilities_hosted?: Array<{ name: string; type: string }>;
        [key: string]: unknown;
    };
    ext: {
        host?: string;
        pid?: number;
        started_at?: string;
        [key: string]: unknown;
    };
}

const statusColors: Record<string, { dot: string; bg: string; text: string }> = {
    online: { dot: 'text-emerald-500', bg: 'bg-emerald-50', text: 'text-emerald-700' },
    draining: { dot: 'text-amber-500', bg: 'bg-amber-50', text: 'text-amber-700' },
    offline: { dot: 'text-slate-400', bg: 'bg-slate-50', text: 'text-slate-500' },
};

function formatRelativeTime(dateStr: string): string {
    const diff = Date.now() - new Date(dateStr).getTime();
    const seconds = Math.floor(diff / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
}

export default function WorkersListPage() {
    usePageTitle('Workers');
    const { setHeaderTitle, setHeaderAction } = useHeaderAction();
    const [workers, setWorkers] = useState<WorkerCapability[]>([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [sortField, setSortField] = useState('name');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
    const colors = capabilityColors.worker;

    const fetchWorkers = async (isRefresh = false) => {
        if (isRefresh) setRefreshing(true);
        else setLoading(true);

        try {
            const res = await api.get('/capabilities', { params: { type: 'worker', include_entitled: 'true' } });
            const payload = res.data;
            const list = Array.isArray(payload)
                ? payload
                : Array.isArray(payload?.data)
                  ? payload.data
                  : [];
            setWorkers(list as WorkerCapability[]);
        } catch {
            setWorkers([]);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        setHeaderTitle({
            icon: (
                <div
                    className={`flex h-7 w-7 items-center justify-center rounded-md ${colors.bgLight} ${colors.text}`}
                >
                    <Server size={16} />
                </div>
            ),
            title: 'Workers',
            subtitle: 'Browse and monitor workers hosting capability runtimes.',
        });

        setHeaderAction(
            <button
                onClick={() => fetchWorkers(true)}
                disabled={refreshing}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-btn-primary px-3 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-btn-primary-hover disabled:opacity-50"
            >
                <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
                Refresh
            </button>,
        );

        return () => {
            setHeaderTitle(null);
            setHeaderAction(null);
        };
    }, [colors.bgLight, colors.text, refreshing, setHeaderAction, setHeaderTitle]);

    useEffect(() => {
        fetchWorkers();
    }, []);

    const handleSort = (field: string) => {
        if (sortField === field) {
            setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const filteredWorkers = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();

        let list = workers.filter((worker) => {
            const hosted = worker.config?.capabilities_hosted ?? [];
            const matchesSearch =
                q.length === 0 ||
                worker.name.toLowerCase().includes(q) ||
                (worker.ext?.host ?? '').toLowerCase().includes(q) ||
                hosted.some((c) => c.name.toLowerCase().includes(q));
            const matchesStatus =
                statusFilter === 'all' || (worker.status ?? 'offline') === statusFilter;
            return matchesSearch && matchesStatus;
        });

        list = [...list].sort((a, b) => {
            let cmp = 0;
            if (sortField === 'status') {
                cmp = (a.status ?? 'offline').localeCompare(b.status ?? 'offline');
            } else if (sortField === 'modified') {
                cmp = new Date(a.modified).getTime() - new Date(b.modified).getTime();
            } else {
                cmp = a.name.localeCompare(b.name);
            }
            return sortDirection === 'asc' ? cmp : -cmp;
        });

        return list;
    }, [workers, searchQuery, statusFilter, sortField, sortDirection]);

    const statusSelect = (
        <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className={cn('w-40', listControlsGlassTrigger)}>
                <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="online">Online</SelectItem>
                <SelectItem value="draining">Draining</SelectItem>
                <SelectItem value="offline">Offline</SelectItem>
            </SelectContent>
        </Select>
    );

    return (
        <CapabilityListPanel<WorkerCapability>
            items={filteredWorkers}
            sortOptions={[
                { key: 'name', label: 'Name' },
                { key: 'status', label: 'Status' },
                { key: 'modified', label: 'Updated' },
            ]}
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            searchPlaceholder="Search workers by name, host, or hosted capability..."
            allTags={[]}
            selectedTags={new Set<string>()}
            onToggleTag={() => {}}
            onClearAllTags={() => {}}
            onSelectAllTags={() => {}}
            showTagsFilter={false}
            extraToolbar={statusSelect}
            isLoading={loading}
            loadingMessage="Loading workers..."
            emptyStateVariant="glass"
            emptyMessage="No workers registered. Start a worker process to see it here."
            gridClassName="grid grid-cols-1 gap-3"
            renderCard={(w) => {
                const status = w.status ?? 'offline';
                const sc = statusColors[status] ?? statusColors.offline;
                const hosted = w.config?.capabilities_hosted ?? [];

                const detailPath = w.app_key
                    ? capabilityDetailPath(w.app_key, 'worker', w.name)
                    : null;

                const card = (
                    <div
                        className={`rounded-lg border border-slate-200 border-l-4 ${colors.border} bg-white p-5 shadow-sm transition-shadow hover:shadow-md`}
                    >
                        <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3">
                                <Server className={`h-5 w-5 ${colors.text}`} />
                                <div>
                                    <div className="flex items-center gap-2">
                                        <h3 className="font-semibold text-slate-900">{w.name}</h3>
                                        {detailPath && (
                                            <ExternalLink size={12} className="text-slate-400" />
                                        )}
                                    </div>
                                    {w.ext?.host && (
                                        <p className="font-mono text-xs text-slate-500">
                                            {w.ext.host}
                                            {w.ext.pid ? `:${w.ext.pid}` : ''}
                                        </p>
                                    )}
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <span
                                    className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${sc.bg} ${sc.text}`}
                                >
                                    <Circle size={8} className={`fill-current ${sc.dot}`} />
                                    {status}
                                </span>
                                <span className="text-xs text-slate-400">
                                    {formatRelativeTime(w.modified)}
                                </span>
                            </div>
                        </div>

                        {hosted.length > 0 && (
                            <div className="mt-3 border-t border-slate-100 pt-3">
                                <p className="mb-2 text-xs font-medium text-slate-500">
                                    Hosting {hosted.length} capabilit
                                    {hosted.length !== 1 ? 'ies' : 'y'}
                                </p>
                                <div className="flex flex-wrap gap-1.5">
                                    {hosted.slice(0, 20).map((c) => (
                                        <span
                                            key={c.name}
                                            className="inline-flex items-center rounded-md bg-slate-50 px-2 py-0.5 text-xs font-mono text-slate-600"
                                        >
                                            {c.name}
                                        </span>
                                    ))}
                                    {hosted.length > 20 && (
                                        <span className="text-xs text-slate-400">
                                            +{hosted.length - 20} more
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                );

                return detailPath ? (
                    <Link key={w.id} to={detailPath} className="block">
                        {card}
                    </Link>
                ) : (
                    <div key={w.id}>{card}</div>
                );
            }}
        />
    );
}
